import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Globe,
  ChevronRight,
  ArrowLeft,
  Users,
  Heart,
  TrendingUp,
  MapPin,
  Layers,
  BookOpen,
  Quote,
  Copy,
  Download,
  Check,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { API, COLORS, ORIENTATION_LABELS, FOUNDER_LABELS, CHART } from "../../lib/constants";

const TOOLTIP_STYLE = CHART.tooltip;

const REGION_META = {
  "North America": { color: "#3b82f6", icon: "NA" },
  "Europe": { color: "#10b981", icon: "EU" },
  "Asia Pacific": { color: "#f59e0b", icon: "AP" },
  "Middle East & North Africa": { color: "#ef4444", icon: "ME" },
  "Sub-Saharan Africa": { color: "#8b5cf6", icon: "AF" },
  "Latin America & Caribbean": { color: "#ec4899", icon: "LA" },
};

export const RegionalExplorer = ({ entityType = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [regionAicci, setRegionAicci] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [res, aicci] = await Promise.allSettled([
          axios.get(`${API}/regional-ecosystem`, { params: entityType ? { entity_type: entityType } : {} }),
          axios.get(`${API}/aicci/regions`),
        ]);
        if (res.status === "fulfilled") setData(res.value.data);
        if (aicci.status === "fulfilled") setRegionAicci(aicci.value.data);
      } catch (e) {
        void 0; // error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [entityType]);

  const selectRegion = useCallback(async (regionName) => {
    setSelectedRegion(regionName);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/regional-ecosystem`, {
        params: { region: regionName, ...(entityType ? { entity_type: entityType } : {}) },
      });
      setDetail(res.data.detail);
    } catch (e) {
      void 0; // error handled silently
    } finally {
      setDetailLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="spinner mr-3" />
        <span className="text-muted-foreground">Loading regional data...</span>
      </div>
    );
  }

  if (!data) return null;

  // Detail view for a specific region
  if (selectedRegion && detail) {
    return (
      <RegionDetail
        detail={detail}
        comparison={data.comparison}
        regionAicci={regionAicci}
        onBack={() => {
          setSelectedRegion(null);
          setDetail(null);
        }}
      />
    );
  }

  if (selectedRegion && detailLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="spinner mr-3" />
        <span className="text-muted-foreground">
          Loading {selectedRegion}...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="regional-explorer">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Globe className="w-6 h-6 text-primary" />
          Regional AI Ecosystem
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Explore how AI innovation differs across continents and regions.
          Click a region to drill down.
        </p>
      </div>

      {/* Region Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.regions.map((r) => {
          const meta = REGION_META[r.region] || { color: "#6b7280", icon: "?" };
          return (
            <button
              key={r.region}
              onClick={() => selectRegion(r.region)}
              data-testid={`region-card-${r.region.replace(/\s/g, "-")}`}
              className="group relative overflow-hidden bg-card border border-border rounded-xl p-5 text-left transition-all hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
            >
              {/* Accent bar */}
              <div
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
                style={{ backgroundColor: meta.color }}
              />

              <div className="flex items-start justify-between mb-3">
                <div>
                  <div
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold mb-2"
                    style={{
                      backgroundColor: meta.color + "20",
                      color: meta.color,
                    }}
                  >
                    {meta.icon}
                  </div>
                  <h3 className="font-semibold text-base">{r.region}</h3>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary transition-colors">
                  <span className="hidden sm:inline">Explore</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-2xl font-bold" style={{ color: meta.color }}>
                    {r.total_apps.toLocaleString()}
                  </div>
                  <div className="text-muted-foreground text-xs">AI Apps</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {r.pct_of_global}%
                  </div>
                  <div className="text-muted-foreground text-xs">of Global</div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" />
                  {r.active_countries}/{r.total_countries} countries active
                </div>
                {(() => {
                  const ra = regionAicci.find(a => a.region === r.region);
                  if (!ra) return null;
                  const c = ra.score >= 60 ? "#34d399" : ra.score >= 40 ? "#f59e0b" : "#f87171";
                  return (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: `${c}10`, border: `1px solid ${c}25` }}>
                      <span className="text-[10px] font-bold text-slate-400">AICCI</span>
                      <span className="text-xs font-bold" style={{ color: c }}>{ra.score}</span>
                    </div>
                  );
                })()}
              </div>

              {/* Top countries preview */}
              {r.top_countries.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.top_countries.slice(0, 3).map((tc) => (
                    <span
                      key={tc.country}
                      className="px-2 py-0.5 rounded-full text-xs bg-muted/60 text-muted-foreground"
                    >
                      {tc.country}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Cross-Region Comparison */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Regional Comparison
        </h3>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.comparison.map((c) => ({
                region: c.region.replace(" & Caribbean", "").replace(" & North Africa", ""),
                "Care %": c.care_pct,
                "Female-Founded %": c.female_pct,
                "Diversity Score": c.diversity_score,
              }))}
              margin={{ bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.gridStroke} />
              <XAxis
                dataKey="region"
                tick={{ ...CHART.axis, fontSize: 10 }}
                stroke={CHART.axisStroke}
                angle={-20}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                tick={CHART.axis}
                stroke={CHART.axisStroke}
                unit="%"
              />
              <Tooltip {...TOOLTIP_STYLE} />
              <Legend wrapperStyle={CHART.legend} />
              <Bar dataKey="Care %" fill="#10b981" radius={CHART.barRadiusV} />
              <Bar
                dataKey="Female-Founded %"
                fill="#ec4899"
                radius={CHART.barRadiusV}
              />
              <Bar
                dataKey="Diversity Score"
                fill="#8b5cf6"
                radius={CHART.barRadiusV}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Diversity Score = Female-Founded + Mixed Teams as % of region total
        </p>
      </div>
    </div>
  );
};

// --- Region Detail View ---
const RegionDetail = ({ detail, comparison, regionAicci = [], onBack }) => {
  const meta = REGION_META[detail.region] || { color: "#6b7280", icon: "?" };
  const regionComp = comparison.find((c) => c.region === detail.region);
  const [spotlight, setSpotlight] = useState(null);
  const [countryAicci, setCountryAicci] = useState([]);

  useEffect(() => {
    const fetchSpotlight = async () => {
      try {
        const res = await axios.get(`${API}/regional-spotlight`, {
          params: { region: detail.region },
        });
        setSpotlight(res.data);
      } catch (e) {
        void 0; // error handled silently
      }
    };
    fetchSpotlight();
    // Fetch country AICCI
    axios.get(`${API}/aicci/countries?limit=30`).then(r => {
      const regionCountries = (detail.countries || []).map(c => c.country);
      setCountryAicci(r.data.filter(c => regionCountries.includes(c.country)));
    }).catch(() => {});
  }, [detail.region, detail.countries]);

  const orientUnknown = detail.orientation?.unknown || 0;
  const orientData = Object.entries(detail.orientation || {})
    .filter(([key]) => key !== "unknown")
    .map(([key, val]) => ({
      name: ORIENTATION_LABELS[key] || key,
      value: val,
      fill: COLORS[key] || COLORS.unknown,
    }));

  const founderUnknown = detail.founders?.unknown || 0;
  const founderData = Object.entries(detail.founders || {})
    .filter(([key]) => key !== "unknown")
    .map(([key, val]) => ({
      name: FOUNDER_LABELS[key] || key,
      value: val,
      fill: COLORS[key] || COLORS.unknown,
    }));

  const countryBarData = (detail.countries || []).slice(0, 12).map((c) => ({
    country: c.country,
    total: c.total,
    care: c.orientation?.care || 0,
    utility: c.orientation?.utility || 0,
    creative: c.orientation?.creative || 0,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="region-detail">
      {/* Back & Header with AICCI top-right */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onBack} data-testid="region-back-btn" className="p-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: meta.color }} />
              <h2 className="text-2xl font-bold">{detail.region}</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              {detail.total_apps.toLocaleString()} AI apps across {detail.countries?.length || 0} countries
            </p>
          </div>
        </div>
        {/* AICCI badge top-right */}
        {(() => {
          const ra = regionAicci.find(a => a.region === detail.region) || (countryAicci.length > 0 ? { score: Math.round(countryAicci.reduce((s, c) => s + c.score, 0) / countryAicci.length) } : null);
          if (!ra) return null;
          const c = ra.score >= 60 ? "#34d399" : ra.score >= 40 ? "#f59e0b" : "#f87171";
          return (
            <div className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl" style={{ background: `${c}08`, border: `1px solid ${c}20` }}>
              <div className="relative w-14 h-14 flex items-center justify-center">
                <svg width="56" height="56" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3.5" />
                  <circle cx="28" cy="28" r="24" fill="none" stroke={c} strokeWidth="3.5" strokeDasharray={`${ra.score * 1.51} ${151 - ra.score * 1.51}`} strokeDashoffset="37.7" strokeLinecap="round" />
                </svg>
                <span className="absolute text-base font-black" style={{ color: c }}>{ra.score}</span>
              </div>
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">AICCI</span>
            </div>
          );
        })()}
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total AI Apps"
          value={detail.total_apps.toLocaleString()}
          color={meta.color}
          icon={<Layers className="w-4 h-4" />}
        />
        <MetricCard
          label="Care-Oriented"
          value={`${detail.care_pct}%`}
          color="#10b981"
          icon={<Heart className="w-4 h-4" />}
        />
        <MetricCard
          label="Female-Founded"
          value={`${detail.female_pct}%`}
          color="#ec4899"
          icon={<Users className="w-4 h-4" />}
        />
        <MetricCard
          label="Diversity Score"
          value={`${regionComp?.diversity_score || 0}%`}
          color="#8b5cf6"
          icon={<TrendingUp className="w-4 h-4" />}
        />
      </div>

      {/* Regional Spotlight */}
      {spotlight && spotlight.narratives?.length > 0 && (
        <SpotlightCard spotlight={spotlight} color={meta.color} />
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Orientation Distribution */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4 text-sm">Value Orientation</h3>
          <div className="space-y-3">
            {orientData.map((item) => {
              const pct = detail.total_apps > 0 ? (item.value / detail.total_apps * 100).toFixed(1) : 0;
              return (
                <div key={item.name} data-testid={`orient-bar-${item.name}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{item.name}</span>
                    <span className="font-medium tabular-nums">{item.value.toLocaleString()} ({pct}%)</span>
                  </div>
                  <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: item.fill }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {orientUnknown > 0 && (
            <RegionalCallout count={orientUnknown} total={detail.total_apps} label="orientation" />
          )}
        </div>

        {/* Founder Distribution */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4 text-sm">Founder Composition</h3>
          <div className="space-y-3">
            {founderData.map((item) => {
              const pct = detail.total_apps > 0 ? (item.value / detail.total_apps * 100).toFixed(1) : 0;
              return (
                <div key={item.name} data-testid={`founder-bar-${item.name}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{item.name}</span>
                    <span className="font-medium tabular-nums">{item.value.toLocaleString()} ({pct}%)</span>
                  </div>
                  <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: item.fill }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {founderUnknown > 0 && (
            <RegionalCallout count={founderUnknown} total={detail.total_apps} label="founder" />
          )}
        </div>
      </div>

      {/* Country Breakdown */}
      {countryBarData.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            Country Breakdown (by Orientation)
          </h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={countryBarData}
                margin={{ bottom: 40 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART.gridStroke}
                />
                <XAxis
                  dataKey="country"
                  tick={{ ...CHART.axis, fontSize: 10 }}
                  stroke={CHART.axisStroke}
                  angle={-25}
                  textAnchor="end"
                  height={50}
                  interval={0}
                />
                <YAxis
                  tick={CHART.axis}
                  stroke={CHART.axisStroke}
                />
                <Tooltip {...TOOLTIP_STYLE} />
                <Legend wrapperStyle={CHART.legend} />
                <Bar
                  dataKey="care"
                  stackId="a"
                  fill={COLORS.care}
                  name="Care"
                />
                <Bar
                  dataKey="utility"
                  stackId="a"
                  fill={COLORS.utility}
                  name="Utility"
                />
                <Bar
                  dataKey="creative"
                  stackId="a"
                  fill={COLORS.creative}
                  name="Creative"
                  radius={CHART.barRadiusV}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Country Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-sm">All Countries in Region</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-3 font-medium">Country</th>
                <th className="text-right p-3 font-medium">Apps</th>
                <th className="text-right p-3 font-medium">Care %</th>
                <th className="text-right p-3 font-medium">Female %</th>
                <th className="text-right p-3 font-medium">Mixed %</th>
              </tr>
            </thead>
            <tbody>
              {(detail.countries || []).map((c, idx) => {
                const carePct =
                  c.total > 0
                    ? ((c.orientation?.care || 0) / c.total * 100).toFixed(1)
                    : "0.0";
                const femalePct =
                  c.total > 0
                    ? ((c.founders?.female_only || 0) / c.total * 100).toFixed(1)
                    : "0.0";
                const mixedPct =
                  c.total > 0
                    ? ((c.founders?.mixed || 0) / c.total * 100).toFixed(1)
                    : "0.0";
                return (
                  <tr
                    key={c.country}
                    className={`border-b border-border/30 ${idx % 2 ? "bg-muted/10" : ""}`}
                  >
                    <td className="p-3 font-medium">{c.country}</td>
                    <td className="p-3 text-right tabular-nums">
                      {c.total.toLocaleString()}
                    </td>
                    <td className="p-3 text-right tabular-nums">{carePct}%</td>
                    <td className="p-3 text-right tabular-nums">
                      {femalePct}%
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {mixedPct}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Country AICCI Leaderboard */}
      {countryAicci.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="country-aicci-leaderboard">
          <h3 className="text-sm font-bold mb-3">Country Conscious Innovation Index (AICCI)</h3>
          <div className="space-y-2">
            {countryAicci.map((c, i) => {
              const scoreColor = c.score >= 60 ? "#34d399" : c.score >= 40 ? "#f59e0b" : "#f87171";
              return (
                <div key={c.country} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-5 font-bold">{i + 1}</span>
                  <span className="text-xs w-24 truncate">{c.country}</span>
                  <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${c.score}%`, background: scoreColor }} />
                  </div>
                  <span className="text-xs font-bold w-10 text-right" style={{ color: scoreColor }}>{c.score}</span>
                  <span className="text-[10px] text-muted-foreground w-14 text-right">{c.total_apps.toLocaleString()} apps</span>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">Based on care orientation, founder diversity, ethical governance, and extraction resistance.</p>
        </div>
      )}
    </div>
  );
};

const SpotlightCard = ({ spotlight, color }) => {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const copyReport = async () => {
    const lines = [
      `REGIONAL AI SPOTLIGHT: ${spotlight.region}`,
      `${"=".repeat(50)}`,
      spotlight.headline,
      "",
      ...spotlight.narratives.flatMap((n) => [
        `${n.title.toUpperCase()} (${n.stat} ${n.stat_label})`,
        n.text,
        "",
      ]),
      `Source: AI Orientation Observatory | ${spotlight.total_apps?.toLocaleString()} apps analyzed`,
      `Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = lines.join("\n");
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadPNG = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#09090b",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `ai-observatory-${spotlight.region?.toLowerCase().replace(/\s+/g, "-")}-spotlight.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      void 0; // error handled silently
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      ref={cardRef}
      className="relative overflow-hidden border rounded-xl"
      style={{ borderColor: color + "40" }}
      data-testid="regional-spotlight"
    >
      {/* Gradient accent */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          background: `linear-gradient(135deg, ${color}, transparent 70%)`,
        }}
      />

      <div className="relative p-6">
        {/* Header with actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" style={{ color }} />
            <span
              className="text-xs font-semibold tracking-wide uppercase"
              style={{ color }}
            >
              Regional Spotlight
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={copyReport}
              data-testid="spotlight-copy-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/40 hover:bg-muted/70 transition-colors"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              {copied ? "Copied" : "Copy Report"}
            </button>
            <button
              onClick={downloadPNG}
              disabled={downloading}
              data-testid="spotlight-download-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/40 hover:bg-muted/70 transition-colors disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" />
              {downloading ? "Saving..." : "Download PNG"}
            </button>
          </div>
        </div>

        {/* Headline */}
        <p
          className="text-lg font-semibold mb-5 leading-snug"
          data-testid="spotlight-headline"
        >
          <Quote
            className="w-4 h-4 inline-block mr-1 opacity-40"
            style={{ color }}
          />
          {spotlight.headline}
        </p>

        {/* Narrative Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {spotlight.narratives.map((n) => (
            <div
              key={n.title}
              className="bg-muted/20 rounded-lg p-4 border border-border/40"
              data-testid={`spotlight-narrative-${n.title}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {n.title}
                </span>
                <span
                  className="text-lg font-bold tabular-nums"
                  style={{ color }}
                >
                  {n.stat}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {n.text}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-[10px] mt-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", color: "#64748b" }}>
          <span style={{ color: "#94a3b8" }}>amplifyingconsciousinnovation.com</span>
          <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, color, icon }) => (
  <div className="bg-card border border-border rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1 text-muted-foreground text-xs">
      {icon}
      {label}
    </div>
    <div className="text-2xl font-bold" style={{ color }}>
      {value}
    </div>
  </div>
);


const RegionalCallout = ({ count, total, label }) => {
  const pct = total > 0 ? (count / total * 100).toFixed(1) : "0";
  return (
    <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs text-muted-foreground">
      <span className="text-amber-400/70 font-semibold tabular-nums">{count.toLocaleString()} ({pct}%)</span>
      <span>unknown {label} excluded from chart</span>
    </div>
  );
};
